import React, { useState } from "react";
import { Helmet } from "react-helmet-async";

const COLORS = {
  navy: "#0A1628",
  deepBlue: "#0D2137",
  salarBlue: "#1B6EC2",
  accent: "#00D4FF",
  gold: "#D4A843",
  white: "#F8FAFB",
  cardBg: "rgba(15, 35, 60, 0.55)",
  cardBgAlt: "rgba(15, 35, 60, 0.40)",
  cardBorder: "rgba(96, 165, 250, 0.25)",
  textPrimary: "#E0F2FE",
  textBody: "#BFDBFE",
  textMuted: "#93C5FD",
  green: "#4ADE80",
  red: "#F87171",
};

const DOCS = [
  {
    id: "SALAR-AIF-CAP-20260430-v3",
    title: "AI Fabric Capability Statement",
    version: "3.0",
    issued: "April 30, 2026",
    status: "External",
    pages: 3,
    sha256: "c7200efad81f730fdf892da6d4a47566c246a2cf789bd131b9c8ef4cb5b962e6",
  },
  {
    id: "SALAR-AIF-QC-20260430-v3",
    title: "AI Fabric Quad Chart",
    version: "3.0",
    issued: "April 30, 2026",
    status: "External",
    pages: 2,
    sha256: "64bd9030de2f26e2d0ca6665325bd6c8c81add03c392e5b58ff1269ffcbd9f31",
  },
  {
    id: "SALAR-P2-BRIEF-20260430-v3",
    title: "P2 Patent Relevance Brief",
    version: "3.0",
    issued: "April 30, 2026",
    status: "External",
    pages: 2,
    sha256: "6aebd9fe1d65ae8f45fbd3a263a5158483fa31d9d6b69e150f6ba2ec3a9c93b0",
  },
  {
    id: "SALAR-AIF-RISKMATRIX-20260430-v1",
    title: "AI Fabric Risk Matrix",
    version: "1.0",
    issued: "April 30, 2026",
    status: "External",
    pages: 2,
    sha256: "pending-publication-hash-update-on-next-release",
  },
];

export default function Verify() {
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);

  const handleVerify = () => {
    const q = query.trim().toLowerCase();
    const found = DOCS.find(
      (d) => d.id.toLowerCase() === q || d.sha256.startsWith(q) || d.sha256 === q
    );
    setResult(found ? { found: true, doc: found } : { found: false });
  };

  return (
    <div style={{ fontFamily: "system-ui, -apple-system, sans-serif", minHeight: "100vh", background: COLORS.navy, color: COLORS.textPrimary }}>
      <Helmet>
        <title>Document Verification | SALAR Technologies LLC</title>
        <meta name="description" content="Verify the authenticity, version, issue date, and SHA-256 integrity hash of any SALAR Technologies published document by document ID or hash." />
        <meta property="og:title" content="SALAR Document Verification" />
        <meta property="og:description" content="Verify SALAR document authenticity, version, and integrity hash." />
        <meta property="og:url" content="https://www.salartechnologies.com/verify" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.salartechnologies.com/verify" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* Header strip */}
      <div style={{ background: COLORS.navy, padding: "16px 24px", display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{
          width: 32, height: 32, background: COLORS.salarBlue, borderRadius: 4,
          display: "grid", placeItems: "center", color: "white", fontWeight: 800, fontSize: 13,
        }}>ST</div>
        <div>
          <div style={{ color: "white", fontWeight: 700, fontSize: 13, letterSpacing: 1 }}>SALAR TECHNOLOGIES LLC</div>
          <div style={{ color: COLORS.accent, fontSize: 9, letterSpacing: 2 }}>DOCUMENT VERIFICATION</div>
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "48px 24px" }}>
        <h1 style={{ fontSize: 28, color: COLORS.white, fontWeight: 700, marginBottom: 8 }}>
          Document Verification
        </h1>
        <p style={{ fontSize: 14, color: COLORS.textBody, lineHeight: 1.6, marginBottom: 32 }}>
          Enter a SALAR document ID (from the barcode) or SHA-256 hash to verify authenticity,
          version, issue date, and integrity.
        </p>

        {/* Search Box */}
        <div style={{ display: "flex", gap: 8, marginBottom: 32 }}>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleVerify()}
            placeholder="Enter document ID or SHA-256 hash..."
            data-testid="verify-input"
            style={{
              flex: 1, padding: "12px 16px", border: `1px solid ${COLORS.cardBorder}`,
              borderRadius: 6, fontSize: 14, fontFamily: "monospace", outline: "none",
            }}
          />
          <button
            onClick={handleVerify}
            data-testid="verify-button"
            style={{
              padding: "12px 24px", background: COLORS.salarBlue, color: "white",
              border: "none", borderRadius: 6, fontWeight: 700, fontSize: 14,
              cursor: "pointer",
            }}
          >
            Verify
          </button>
        </div>

        {/* Result */}
        {result && (
          <div data-testid="verify-result" style={{
            background: COLORS.cardBg, borderRadius: 10,
            border: `2px solid ${result.found ? COLORS.green : COLORS.red}`,
            padding: "24px 28px", marginBottom: 32,
          }}>
            {result.found ? (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                  <span style={{ fontSize: 24 }}>✅</span>
                  <span style={{ fontSize: 16, fontWeight: 700, color: COLORS.green }}>Document Verified</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: "8px 16px", fontSize: 13 }}>
                  <div style={{ fontWeight: 700, color: COLORS.white }}>Document ID</div>
                  <div style={{ fontFamily: "monospace", color: COLORS.textBody }}>{result.doc.id}</div>
                  <div style={{ fontWeight: 700, color: COLORS.white }}>Title</div>
                  <div style={{ color: COLORS.textBody }}>{result.doc.title}</div>
                  <div style={{ fontWeight: 700, color: COLORS.white }}>Version</div>
                  <div style={{ color: COLORS.textBody }}>{result.doc.version}</div>
                  <div style={{ fontWeight: 700, color: COLORS.white }}>Issued</div>
                  <div style={{ color: COLORS.textBody }}>{result.doc.issued}</div>
                  <div style={{ fontWeight: 700, color: COLORS.white }}>Status</div>
                  <div style={{ color: COLORS.textBody }}>{result.doc.status}</div>
                  <div style={{ fontWeight: 700, color: COLORS.white }}>Pages</div>
                  <div style={{ color: COLORS.textBody }}>{result.doc.pages}</div>
                  <div style={{ fontWeight: 700, color: COLORS.white }}>SHA-256</div>
                  <div style={{ fontFamily: "monospace", fontSize: 11, color: COLORS.textBody, wordBreak: "break-all" }}>{result.doc.sha256}</div>
                </div>
              </>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 24 }}>❌</span>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.red }}>Document Not Found</div>
                  <div style={{ fontSize: 13, color: COLORS.textBody, marginTop: 4 }}>
                    The document ID or hash you entered does not match any verified SALAR document.
                    Contact vikrant@salartechnologies.com for assistance.
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Registry */}
        <h2 style={{ fontSize: 18, fontWeight: 700, color: COLORS.white, marginBottom: 16 }}>Published Document Registry</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {DOCS.map((doc) => (
            <div
              key={doc.id}
              data-testid={`verify-doc-${doc.id}`}
              style={{
                background: COLORS.cardBg, borderRadius: 8, border: `1px solid ${COLORS.cardBorder}`,
                padding: "16px 20px", cursor: "pointer",
              }}
              onClick={() => { setQuery(doc.id); setResult({ found: true, doc }); }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.white }}>{doc.title}</div>
                  <div style={{ fontSize: 11, fontFamily: "monospace", color: COLORS.textMuted, marginTop: 2 }}>{doc.id}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 11, color: COLORS.textBody }}>v{doc.version}</div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted }}>{doc.issued}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 40, padding: "16px 20px", background: `${COLORS.salarBlue}08`, borderRadius: 8, border: `1px solid ${COLORS.salarBlue}15` }}>
          <p style={{ fontSize: 12, color: COLORS.textBody, lineHeight: 1.6, margin: 0 }}>
            <strong style={{ color: COLORS.white }}>How verification works:</strong> Every SALAR document carries a QR code
            (linking to this page) and a Code 128 barcode (encoding the document ID). The SHA-256 hash is computed at
            publication time and recorded here. To verify a document: scan the QR code, enter the barcode ID or compute
            the file&apos;s SHA-256 hash, and compare against this registry.
          </p>
        </div>
      </div>
    </div>
  );
}
